import Head from 'next/head';
import Link from 'next/link';
import React, { useState } from 'react';

import parse from 'html-react-parser';
import { Form, Formik } from 'formik';

import { resetPassword } from '../../api-service/user';
import { WEBSITE_NAME } from '../../lib/constants';
import FormInput from '../../components/form-input';
import AnimateSpinner from '../../components/animate-spinner';
import { forgotPasswordSchema } from '../../lib/schemas';

const ForgotPassword = () => {
  const [error, setError] = useState('');
  const [isPasswordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [passwordResetIsLoading, setPasswordResetIsLoading] = useState(false);

  const [activeField, setActiveField] = useState('');

  const handleFocus = (e) => {
    setActiveField(e.target.name);
  };

  const handleBlur = (e) => {
    if (activeField === e.target.name) setActiveField('');
  };

  const onForgotPassword = async (values: { email: string }) => {
    setPasswordResetIsLoading(true);
    const result = await resetPassword(values);
    setPasswordResetIsLoading(false);

    if (result.isSuccess) {
      setPasswordResetSuccess(true);
    } else {
      setError(result.message);
    }
  };

  return (
    <div>
      <Head>
        <title>
          {'Forgot Password | '}
          {WEBSITE_NAME}
        </title>
      </Head>
      <div className="bg-gray-50 flex flex-col justify-center py-24 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="text-center text-3xl font-extrabold text-gray-900 ">
            {'Forgot password'}
          </h2>

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              <div className="space-y-6">
                <div className="text-center text-xs">
                  {isPasswordResetSuccess ? (
                    <div>
                      <p className="text-sm mb-3">
                        Please check your email for a link to reset your
                        password.
                      </p>
                    </div>
                  ) : (
                    'Please enter your email address. You will receive an email message with instructions on how to reset your password.'
                  )}
                </div>

                {passwordResetIsLoading && <AnimateSpinner />}

                {!isPasswordResetSuccess && !passwordResetIsLoading && (
                  <div className="space-y-6 mt-6">
                    <div className="mb-4 text-xs space-y-6">
                      <Formik
                        initialValues={{
                          email: ''
                        }}
                        validationSchema={forgotPasswordSchema}
                        onSubmit={async (values) => {
                          onForgotPassword(values);
                        }}
                      >
                        {({ isSubmitting }) => (
                          <Form className="ffh-form">
                            <FormInput
                              name="email"
                              label="Email"
                              smallWidth="full"
                              largeWidth="full"
                              onBlur={handleBlur}
                              onFocus={handleFocus}
                              activeField={activeField}
                            />
                            <button
                              type="submit"
                              className="shadow bg-blue-700 hover:bg-purple-400 focus:shadow-outline focus:outline-none text-white font-bold py-3 px-4 rounded w-full "
                              disabled={isSubmitting}
                            >
                              {'Reset my password'}
                            </button>
                            {error && (
                              <div className="text-center text-xs text-red-600 mt-2">
                                {parse(error)}
                              </div>
                            )}
                            <div className="text-sm text-right mt-6">
                              <Link
                                href="/auth/login"
                                className="font-medium text-indigo-600 hover:text-indigo-500"
                              >
                                {'Back to login'}
                              </Link>
                            </div>
                          </Form>
                        )}
                      </Formik>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="text-center text-xs text-red-600">
                    {parse(error)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
